import React from 'react';
import { Row, Col, Table, Button } from 'react-bootstrap';

// Same STRIDE colors/labels used in the Threat Model Viewer, for visual consistency
const STRIDE_COLOR = {
  S: '#7c3aed',
  T: '#ea580c',
  R: '#0891b2',
  I: '#2563eb',
  D: '#dc2626',
  E: '#16a34a',
};
const STRIDE_LABEL = {
  S: 'Spoofing',
  T: 'Tampering',
  R: 'Repudiation',
  I: 'Information Disclosure',
  D: 'Denial of Service',
  E: 'Elevation of Privilege',
};

export function StrideBadges({ letters }) {
  return (
    <div className="d-flex flex-wrap gap-1">
      {letters.map(k => (
        <span key={k} title={STRIDE_LABEL[k]} style={{
          backgroundColor: STRIDE_COLOR[k], color: '#fff',
          padding: '1px 7px', borderRadius: 6,
          fontSize: 11.5, fontWeight: 700, cursor: 'default',
        }}>{k}</span>
      ))}
    </div>
  );
}

export function DesignDocumentationSection() {
  return (
    <>
      <h5 className="mt-4">Software Design Documentation</h5>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">Use-Case Diagram</h6>
          <p>
            A use-case diagram is a high-level representation used in software design to show how external actors interact with a system.
            It focuses on functional requirements—what the system should do from the user's perspective.
          </p>
          <div className="border p-3 bg-light">
            <img
              src="/images/usecase_example.jpg"
              alt="Use-case diagram example"
              style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }}
            />
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">C4 Context Diagram</h6>
          <p>
            The C4 Context diagram provides a high-level view of the system and its relationships with external users and systems.
            It shows who uses the system and what other systems it interacts with, focusing on the big picture without technical details.
          </p>
          <div className="border p-3 bg-light">
            <img
              src="/images/c4_context.jpg"
              alt="C4 Context diagram example"
              style={{ width: '100%', maxHeight: 500, objectFit: 'contain' }}
            />
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">C4 Container Diagram</h6>
          <p>
            The C4 Container diagram zooms into the system to show the high-level technical building blocks (containers) such as web applications,
            databases, mobile apps, and microservices. It illustrates how these containers communicate and what technologies are used.
          </p>
          <div className="border p-3 bg-light">
            <img
              src="/images/c4_container.jpg"
              alt="C4 Container diagram example"
              style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }}
            />
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">C4 Component Diagram</h6>
          <p>
            The C4 Component diagram zooms into an individual container to show its internal structure.
            It maps out the key components (classes, modules, or services) within the container and how they interact with each other.
          </p>
          <div className="border p-3 bg-light">
            <img
              src="/images/c4_component.jpg"
              alt="C4 Component diagram example"
              style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }}
            />
          </div>
        </Col>
      </Row>
    </>
  );
}

export function ThreatModelSection() {
  return (
    <>
      <h5 className="mt-4">Security Threat Models</h5>
      <p>
        <i>"Threat modeling is analyzing <strong>representations</strong> of a system to highlight concerns about <strong>security and privacy</strong> characteristics." -- <a href="https://www.threatmodelingmanifesto.org/" target="_blank" rel="noopener noreferrer">Threat Modeling Manifesto</a></i>
      </p>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">Data Flow Diagram (DFD) -- a representation of a system</h6>
          <p>
            A Data Flow Diagram (DFD) shows how data moves through a system by illustrating processes, data stores,
            data flows, and external entities. It helps visualize the system's data architecture and identify potential
            security boundaries and trust zones.
          </p>
          <div className="border p-3 bg-light">
            <img
              src="/images/dfd_example.jpg"
              alt="Data Flow Diagram example"
              style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }}
            />
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">STRIDE Threat Model</h6>
          <p>
            STRIDE is a threat modeling framework developed by Microsoft that helps identify security threats by categorizing them into six types:
          </p>
          <ul className="mb-3">
            <li><strong>S</strong>poofing - Pretending to be someone or something else</li>
            <li><strong>T</strong>ampering - Modifying data or code</li>
            <li><strong>R</strong>epudiation - Denying having performed an action</li>
            <li><strong>I</strong>nformation Disclosure - Exposing information to unauthorized parties</li>
            <li><strong>D</strong>enial of Service - Degrading or denying service to users</li>
            <li><strong>E</strong>levation of Privilege - Gaining unauthorized access or permissions</li>
          </ul>
          <p>
            <strong>STRIDE-per-Element:</strong> This approach applies STRIDE analysis to each element in a Data Flow Diagram.
            Different DFD elements (external entities, processes, data stores, and data flows) are susceptible to different types of threats.
            By systematically analyzing each element, security professionals can identify specific vulnerabilities and design appropriate countermeasures.
          </p>
          <div className="border p-3 bg-light mb-3">
            <Table striped bordered hover responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>DFD Element</th>
                  <th>Applicable STRIDE Letters</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Process</td>
                  <td><StrideBadges letters={['S', 'T', 'R', 'I', 'D', 'E']} /></td>
                </tr>
                <tr>
                  <td>Data Store</td>
                  <td><StrideBadges letters={['T', 'I', 'D']} /></td>
                </tr>
                <tr>
                  <td>External Entity</td>
                  <td><StrideBadges letters={['S', 'R']} /></td>
                </tr>
                <tr>
                  <td>Data Flow</td>
                  <td><StrideBadges letters={['T', 'I', 'D']} /></td>
                </tr>
                <tr>
                  <td>Trust Boundary</td>
                  <td className="text-muted">Not separately threat-modeled — informs attacker selection</td>
                </tr>
              </tbody>
            </Table>
          </div>
          <p className="mb-2"><strong>Example Threat Analysis:</strong></p>
          <div className="border p-3 bg-light">
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Threat ID</th>
                  <th>Threat Statement</th>
                  <th>Element</th>
                  <th>STRIDE</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>T1</td>
                  <td>An attacker impersonates a legitimate customer to access booking functions.</td>
                  <td>Customer (EE)</td>
                  <td><strong>S – Spoofing</strong></td>
                </tr>
                <tr>
                  <td>T2</td>
                  <td>An attacker alters movie information flowing between the Movie Service and the database.</td>
                  <td>Data Flow</td>
                  <td><strong>T – Tampering</strong></td>
                </tr>
                <tr>
                  <td>T3</td>
                  <td>A customer denies making a booking request because the system lacks robust logging or audit trails.</td>
                  <td>Movie Service (Process)</td>
                  <td><strong>R – Repudiation</strong></td>
                </tr>
                <tr>
                  <td>T4</td>
                  <td>Sensitive movie schedule or booking data stored in the database is exposed due to insufficient access controls.</td>
                  <td>Movie Database (Data Store)</td>
                  <td><strong>I – Information Disclosure</strong></td>
                </tr>
                <tr>
                  <td>T5</td>
                  <td>An attacker floods the Movie Service with excessive requests, preventing legitimate access.</td>
                  <td>Movie Service (Process)</td>
                  <td><strong>D – Denial of Service</strong></td>
                </tr>
                <tr>
                  <td>T6</td>
                  <td>A customer exploits a flaw in the Movie Service to gain admin-level actions, such as modifying schedules.</td>
                  <td>Movie Service (Process)</td>
                  <td><strong>E – Elevation of Privilege</strong></td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <h6 className="mb-2">Risk Scoring</h6>
          <p>
            Each threat is scored using a variant of the <strong>OWASP Risk Rating Methodology</strong>. You will see these
            metrics on every threat card in the Threat Model Viewer:
          </p>
          <ul className="mb-3">
            <li>⚡ <strong>Likelihood</strong> — how likely the threat is to succeed, based on the attacker's profile and how easy the vulnerability is to find and exploit</li>
            <li>⚙ <strong>Technical Impact</strong> — the damage a successful exploit would cause (confidentiality, integrity, availability, accountability)</li>
            <li>📈 <strong>Business Impact</strong> — the organizational consequences (financial, reputational, compliance, privacy) — informational only, does not drive Severity</li>
            <li>⚠ <strong>Risk Score</strong> — Likelihood × Technical Impact, used to rank threats numerically</li>
            <li>🚦 <strong>Severity</strong> — LOW / MEDIUM / HIGH / CRITICAL, derived from a 3×3 Likelihood × Technical Impact matrix</li>
          </ul>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => window.open('/documents/risk-rating-methodology.pdf', '_blank')}
          >
            📄 View Full Risk Rating Methodology (PDF)
          </Button>
        </Col>
      </Row>
    </>
  );
}

export default function DocumentationIntroContent({ threatModelProvided }) {
  return (
    <div>
      <p>
        This section covers the types of documentation and design materials provided for your assigned target.
        Use it as a reference while reviewing documents or during the pentest.
      </p>

      <DesignDocumentationSection />

      {threatModelProvided && <ThreatModelSection />}
    </div>
  );
}
